import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';

export default function AuthModal({ open, onClose, onGoogle, onRegister, onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (mode === 'register' && !form.name)) {
      toast.error('Please fill in all fields.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'register') {
        await onRegister({ name: form.name, email: form.email, password: form.password });
        toast.success('Welcome! Your account is ready.');
      } else {
        await onLogin({ email: form.email, password: form.password });
        toast.success('Signed in.');
      }
      setForm({ name: '', email: '', password: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        onInteractOutside={() => onClose()}
        onEscapeKeyDown={() => onClose()}
        onPointerDownOutside={() => onClose()}
      >
        <div className="p-7 sm:p-8">
          <DialogTitle className="sr-only">Sign in to bytesizePM</DialogTitle>
          <div className="font-display text-[20px] font-bold text-[#0E1726]">
            bytesize<span className="text-[#2E6BFF]">PM</span>
          </div>
          <h2 className="font-display font-extrabold text-[#0E1726] text-[24px] mt-3">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </h2>
          <p className="text-[#5B6472] text-sm mt-1">
            {mode === 'login' ? 'Sign in to post, enroll and manage your digest.' : 'Join with any email — Gmail or your own.'}
          </p>

          <button
            type="button"
            onClick={onGoogle}
            className="mt-6 w-full border border-black/15 rounded-full py-3 font-semibold text-[#0E1726] hover:bg-black/5 transition-colors flex items-center justify-center gap-2"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px bg-black/10 flex-1" />
            <span className="text-xs text-[#5B6472]">or with email</span>
            <span className="h-px bg-black/10 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === 'register' && (
              <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
            )}
            <Input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" />
            <Input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" />
            <button type="submit" disabled={busy} className="w-full bg-[#0E1B2E] text-white font-semibold py-3 rounded-full hover:bg-[#1b2a44] transition-colors disabled:opacity-60">
              {busy ? 'Please wait...' : mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>

          <p className="text-sm text-[#5B6472] text-center mt-5">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[#2E6BFF] font-semibold hover:underline">
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.4 2 8 6.9 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 46c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 36.9 26.9 38 24 38c-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C7.7 41.1 15.2 46 24 46z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.5 5.5C41.9 36 45 30.7 45 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}
